import config from "@config";

const base = config.backendServer;

const apiEndpoints = {
  // ─── Authentication ────────────────────────────────────────────────────────
  auth: {
    login: () => `${base}login/`,
    tokenVerify: () => `${base}token/verify/`,
    changePassword: () => `${base}password/staff/change/`,
    commercialRegistration: () => `${base}commercial-registration/`,
  },

  // ─── Employee / Staff ──────────────────────────────────────────────────────
  employee: {
    list: () => `${base}employee/`,
    listByRole: (role) => `${base}employee/?role=${role}`,
    listExcludeRoles: (roles) => `${base}employee/?exclude_roles=${encodeURIComponent(roles)}`,
    dropdown: () => `${base}employee/dropdown/`,
    detail: (id) => `${base}employee/${id}/`,
    update: (id) => `${base}employee/${id}/`,
    verify: () => `${base}employee/verify/`,
    changePassword: (id) => `${base}employee/${id}/change_password/`,
  },

  // ─── Tickets / Warranties ──────────────────────────────────────────────────
  ticket: {
    list: () => `${base}ticket/`,
    contractorList: () => `${base}ticket/contractor/`,
    detail: (id) => `${base}ticket/${id}/`,
    update: (id) => `${base}ticket/${id}/`,
    // RMAS-128 - not live on the backend yet; Advance still uses `status`.
    advance: (id) => `${base}ticket/${id}/advance/`,
    contractorDetail: (id) => `${base}ticket/contractor/${id}/`,
    create: () => `${base}ticket/new/`,
    approve: (id) => `${base}ticket/${id}/approved/`,
    reject: (id) => `${base}ticket/${id}/rejected/`,
    status: (id) => `${base}ticket/${id}/status/`,
    assign: (id) => `${base}ticket/${id}/assign/`,
    refund: (id) => `${base}ticket/${id}/refund/`,
    setSolvup: (id) => `${base}ticket/${id}/set-solvup/`,
    setInboundTicket: (id) => `${base}ticket/${id}/set-inbound-ticket/`,
    setOutboundTicket: (id) => `${base}ticket/${id}/set-outbound-ticket/`,
    setDoa: (id) => `${base}ticket/${id}/dead-on-arrival/`,
    setDueDate: (id) => `${base}ticket/${id}/set-due-date/`,
    trackingNumber: (id) => `${base}ticket/${id}/tracking-number/`,
    changeType: (id) => `${base}ticket/${id}/change-type/`,
    address: (id) => `${base}ticket/${id}/address/`,
    replacement: (id) => `${base}ticket/${id}/replacement/`,
    note: (id) => `${base}ticket/${id}/note/`,
    serviceCenter: (id) => `${base}ticket/${id}/service_center/`,
    serviceCenterCentral: (id) => `${base}ticket/${id}/service_center_central/`,
    returnLabel: (id) => `${base}ticket/${id}/return-label/`,
    returnLabelUrl: (id) => `${base}ticket/${id}/return-label-url/`,
    commercialNotes: (ticketId) => `${base}ticket/${ticketId}/commercial-note/`,
    addCommercialNote: (ticketId) => `${base}ticket/${ticketId}/commercial_note/`,
    addNewWarranty: () => `${base}addnewwarranty`,
    getEmailLogs: (ticketId) => `${base}email-logs/?ticket_id=${ticketId}`,
    resendEmailLogs: (id) => `${base}email-logs/${id}/resend/`,
    changeLocation: (ticketId) => `${base}ticket/${ticketId}/change-location/`,
    ticketReasonBrands: (brandId) => `${base}ticket-reasons/${brandId ? `?brandId=${brandId}` : ""}`,
    updateTicketReason: (id) => `${base}ticket-reason/${id}/`,
    deleteTicketReason: (id) => `${base}ticket-reason/${id}/`,
  },

  // ─── Commercial Notes ──────────────────────────────────────────────────────
  commercialNote: {
    detail: (id) => `${base}commercial-note/${id}`,
  },

  // ─── Devices ──────────────────────────────────────────────────────────────
  device: {
    list: () => `${base}devices/`,
    listByBrand: (brandId) => `${base}devices/?brandId=${brandId}`,
    dropdown: () => `${base}device/dropdown/`,
    dropdownByBrand: (brandId) => `${base}device/dropdown/?brandId=${brandId}`,
    detail: (id) => `${base}device/${id}/`,
    categories: () => `${base}device-categories/`,
    getDevicesLegacy: () => `${base}getdevices`,
    export: () => `${base}device/export/`,
    import: () => `${base}device/import/`,
    listDeviceCin7: (sku) => `${base}cin7/products/?sku=${sku}`,
    bulk: () => `${base}device/bulk/`,
  },

  // ─── User Devices ──────────────────────────────────────────────────────────
  userDevice: {
    delete: (id) => `${base}user_device/${id}/delete/`,
  },

  // ─── Inventory ─────────────────────────────────────────────────────────────
  inventory: {
    list: () => `${base}inventories/`,
    listByBrand: (brandId) => `${base}inventories/?brandId=${brandId}`,
    histories: (id) => `${base}inventories/${id}/histories`,
    bulkUpload: (brandId) => `${base}inventories/bulk_upload/?brandId=${brandId}`,
  },

  // ─── Service Centers ──────────────────────────────────────────────────────
  serviceCenter: {
    dropdown: () => `${base}service_center/dropdown/`,
  },

  serviceCenterCentral: {
    list: (queryString = "") => `${base}service_center_centrals/${queryString ? `?${queryString}` : ""}`,
    create: () => `${base}service_center_central/`,
    dropdown: () => `${base}service_center_central/dropdown/`,
    detail: (id) => `${base}service_center_central/${id}`,
    locationDropdown: (id) => `${base}service_center_central/${id}/service_center/dropdown/`,
    location: (id) => `${base}service_center_central/${id}/location/`,
  },

  defaultServiceCenter: {
    list: () => `${base}default-service-centers/`,
    detail: (id) => `${base}default-service-center/${id}/`,
    create: () => `${base}default-service-center/`,
  },

  // ─── Store / Location ──────────────────────────────────────────────────────
  storeLocation: {
    dropdown: () => `${base}store_location/dropdown/`,
    dropdownByBrand: (brandId) => `${base}store_location/dropdown/?brandId=${brandId}`,
    commercialStaff: (storeLocationId) => `${base}store_location/${storeLocationId}/commercial_staff/`,
    addStaff: (storeLocationId) => `${base}store_location/${storeLocationId}/add_staff/`,
    removeStaff: (storeLocationId, staffId) => `${base}store_location/${storeLocationId}/remove_staff/${staffId}/`,
  },

  location: {
    states: (country = "") => `${base}location/state/${country ? `?country=${country}` : ""}`,
    suburbs: (state, availableAt = "", query = "") =>
      `${base}location/suburb/?${availableAt ? `available_at=${availableAt}&` : ""}state=${state}&page_size=100${query ? `&q=${encodeURIComponent(query)}` : ""}`,
    postcodes: (suburb, state, availableAt = "") =>
      `${base}location/postcode/?${availableAt ? `available_at=${availableAt}&` : ""}suburb=${suburb}&state=${state}`,
  },

  // ─── Brands ───────────────────────────────────────────────────────────────
  brand: {
    list: (include) => `${base}brands/${include ? `?include_inactive=${include}` : ""}`,
    detail: (id) => `${base}brand/${id}/`,
  },

  // ─── Roles ────────────────────────────────────────────────────────────────
  role: {
    list: () => `${base}roles/`,
  },

  // ─── Customers ────────────────────────────────────────────────────────────
  customer: {
    find: () => `${base}find/customers/`,
    detail: (id) => `${base}customers/${id}/`,
    devices: (id) => `${base}customers/${id}/devices/`,
    convertToCommercial: () => `${base}convert-customer-to-commercial/`,
  },

  // ─── Sellers / Customers ──────────────────────────────────────────────────
  seller: {
    list: () => `${base}sellers/`,
    create: () => `${base}seller/`,
    detail: (id) => `${base}seller/${id}/`,
    sellerAssignBrands: () => `${base}sellers/assign-brands/`,
    storeLocations: (sellerId) => `${base}seller/${sellerId}/store_locations/`,
    storeLocation: (sellerId, storeId) => `${base}seller/${sellerId}/store_location/${storeId}/`,
    addStoreLocation: (sellerId) => `${base}seller/${sellerId}/store_location/`,
  },

  storeLocations: {
    list: () => `${base}store_locations/`,
  },

  // ─── Users (legacy endpoints) ─────────────────────────────────────────────
  user: {
    list: () => `${base}getusers`,
    detail: () => `${base}getsingleuser`,
    delete: () => `${base}deleteuser`,
    forceVerify: () => `${base}forceverify`,
  },

  // ─── Booking / Shipping ───────────────────────────────────────────────────
  booking: {
    transdirect: {
      label: (ticketId) => `${base}booking/transdirect/label/?ticket_id=${ticketId}`,
      sendEmail: () => `${base}booking/transdirect/send_email/`,
      detail: (ticketId) => `${base}booking/transdirect/${ticketId}`,
    },
    auspost: {
      label: (ticketId) => `${base}booking/auspost/label/?ticket_id=${ticketId}`,
      sendEmail: () => `${base}booking/auspost/send_email/`,
      detail: (ticketId) => `${base}booking/auspost/${ticketId}`,
    },
  },

  // ─── Dashboard ────────────────────────────────────────────────────────────
  dashboard: {
    get: () => `${base}getdashboard`,
    averageDaysToClose: () => `${base}dashboard/average-days-to-close/`,
    newTicket: () => `${base}dashboard/new-ticket/`,
    openTicketAllocation: () => `${base}dashboard/open-ticket-allocation-chart/`,
    allocatedOpenTicket: () => `${base}dashboard/allocated-open-ticket-chart/`,
    openTicketDueDates: () => `${base}dashboard/open-ticket-due-dates-chart/`,
    ticketStatus: () => `${base}dashboard/ticket-status-chart/`,
    numberOfDevice: () => `${base}dashboard/number-of-device-chart/`,
    typeOfServiceTicket: () => `${base}dashboard/type-of-service-ticket-chart/`,
    openClosedTicket: () => `${base}dashboard/open-closed-ticket-chart/`,
    warrantyTicket: () => `${base}dashboard/warranty-ticket-chart/`,

    // ─── RMA Portal (Ticket New page) ───
    performanceOverview: () => `${base}dashboard/performance-overview/`,
    teamVelocity: () => `${base}dashboard/team-velocity/`,
    caseSnapshot: () => `${base}dashboard/case-snapshot/`,
  },

  // ─── Ticket list ──────────────────────────────────────────────────────────
  // The plural `tickets/` collection - a different resource from the singular
  // `ticket/` group above. Query params are passed through protectedFetch.
  tickets: {
    list: () => `${base}tickets/`,
  },

  // ─── Customer portal (New case, Ticket New page) ──────────────────────────
  // Same public-facing endpoints the standalone customer portal posts to.
  // Reused here so staff can lodge a request on a customer's behalf without
  // leaving the admin app.
  portal: {
    createRequest: () => `${base}ticket/public-create/`,
    uploadAttachment: () => `${base}ticket/public-create/attachments/`,
    retailers: () => `${base}ticket/public-status-lookup/sellers/`,
    storeLocations: (sellerId) =>
      `${base}ticket/public-status-lookup/store-locations/${sellerId ? `?seller_id=${sellerId}` : ""}`,
    brands: (search) =>
      `${base}ticket/public-status-lookup/brands/${search ? `?q=${encodeURIComponent(search)}` : ""}`,
    devices: (brandId, search) => {
      const params = new URLSearchParams();
      if (brandId) params.set("brand_id", brandId);
      if (search) params.set("q", search);
      const qs = params.toString();
      return `${base}ticket/public-status-lookup/devices/${qs ? `?${qs}` : ""}`;
    },
  },

  notification: {
    list: () => `${base}notifications/`
  }
};

export default apiEndpoints;
